/**
 * Side bar implementation.
 *
 * Copyright © Ircam Amplify 2025-2026
 */
import { EXTERNAL_URLS } from '@/constant/externalUrl'
import { useState } from 'react'
import { AiOutlineBarChart, AiOutlineHome } from 'react-icons/ai'
import { BiBot, BiPlus } from 'react-icons/bi'
import { FaChevronDown, FaChevronUp, FaRegQuestionCircle } from 'react-icons/fa'
import { FiChevronLeft, FiChevronRight, FiLayers } from 'react-icons/fi'
import { MdWebhook } from 'react-icons/md'
import { PiPuzzlePiece } from 'react-icons/pi'
import { NavLink } from 'react-router-dom'

import {
  getLayoutState,
  setModuleSelectorCurrentPageId,
  setSideBarCollapsed,
  useDispatch,
  useSelector,
} from '../store'
import { isDebugMockup } from '../utils/TestUtil'
import Logo from './Logo'
import PopOver from './PopOver'

interface Props {
  Icon: any
  link: string
  label: string
}

function SideBarItem({ Icon, link, label }: Props) {
  const { sideBarCollapsed } = useSelector(getLayoutState)
  return (
    <div className="relative h-10">
      <NavLink
        to={link}
        className={`flex items-center h-10 p-2 ${
          sideBarCollapsed ? 'w-fit' : 'w-full'
        } rounded-lg text-gray-300 hover:text-white group`}
      >
        {!sideBarCollapsed && (
          <>
            <Icon size={24} />
            <span className="ml-3 overflow-hidden whitespace-nowrap">
              {label}
            </span>
          </>
        )}
        {sideBarCollapsed && (
          <PopOver link={link}>
            <Icon size={24} />
            <div className="popover-right w-fit">
              <div className="left-arrow left-arrow-small" />
              <div className="grid rounded-lg p-4 w-fit bg-gray-100 whitespace-nowrap font-normal text-white">
                {label}
              </div>
            </div>
          </PopOver>
        )}
      </NavLink>
    </div>
  )
}

const SideBarCTA = ({ Icon, link, label }: Props) => {
  const dispatch = useDispatch()
  const { sideBarCollapsed } = useSelector(getLayoutState)
  return (
    <div className="sidebar-cta relative h-16 mb-6">
      <div onClick={() => dispatch(setModuleSelectorCurrentPageId(0))}>
        <NavLink
          to={link}
          className={`flex items-center justify-center p-2 ${
            sideBarCollapsed ? 'w-fit' : 'w-11/12 h-14'
          } rounded-lg text-[#232323] bg-[#EE7E6B] hover:text-white group`}
        >
          {!sideBarCollapsed && (
            <>
              <Icon size={24} />
              <span className="ml-3 overflow-hidden whitespace-nowrap">
                {label}
              </span>
            </>
          )}
          {sideBarCollapsed && (
            <PopOver link={link}>
              <Icon size={24} />
              <div className="popover-right w-fit">
                <div className="left-arrow left-arrow-small" />
                <div className="grid rounded-lg p-4 w-fit bg-gray-100 whitespace-nowrap font-normal text-white">
                  {label}
                </div>
              </div>
            </PopOver>
          )}
        </NavLink>
      </div>
    </div>
  )
}

const SideBarHeader = () => {
  const dispatch = useDispatch()
  const { sideBarCollapsed } = useSelector(getLayoutState)

  // Toggle sidebar collapse state when clicking the header
  const onClick = () => {
    dispatch(setSideBarCollapsed(!sideBarCollapsed))
  }

  const ToggleIcon = sideBarCollapsed ? FiChevronRight : FiChevronLeft
  return (
    <div
      id="sidebar-header"
      className="mb-10 flex flex-row"
      onClick={onClick}
      onKeyUp={onClick}
      role="button"
      tabIndex={0}
    >
      <div id="sidebar-logo" className={`w-4/6 flex-1 ${sideBarCollapsed && 'pl-2'}`}>
        <Logo
          color={isDebugMockup() ? "red":"white"}
          alternative={sideBarCollapsed}
          className="transition-opacity duration-250"
          width={sideBarCollapsed ? 30 : 150}
          height={sideBarCollapsed ? 30 : 50}
        />
      </div>
      <div id="sidebar-toggle" className="w-1/6 flex-1 text-gray-300 relative">
        <ToggleIcon className="absolute right-[-1.5rem] inset-y-0 top-2 bg-gray-100 rounded-xl h-8 w-8 p-1 text-gray-300" />
      </div>
    </div>
  )
}

function SideBarSeparator() {
  return (
    <div id="separator" className="border-t border-gray-200 mt-5 mb-5 mr-5" />
  )
}

// Dropdown menu for ressources when sidebar is collapsed
function CollapsedAddMenu({ children = [] }: { children: any }) {
  return (
    <div className="relative flex mb-6 text-gray-300 hover:text-white">
      <PopOver>
        <FiLayers size={24} className="ml-2 my-4" />
        <div className="popover-right w-fit">
          <div className="left-arrow left-arrow-small" />
          <div className="grid rounded-lg p-4 w-fit bg-gray-100 whitespace-nowrap font-normal">
            <div className="font-semibold ml-2 mb-2">Ressources</div>
            {children}
          </div>
        </div>
      </PopOver>
    </div>
  )
}

// Dropdown menu for ressources when sidebar is expanded
function SideBarAddMenu({ children = [] }: { children: any }) {
  const [isOpen, setIsOpen] = useState(false)
  const OpenIcon = isOpen ? FaChevronUp : FaChevronDown
  return (
    <div
      id="sidebar-resources-menu"
      className={`${isOpen ? 'text-primary-300' : 'text-gray-300'} text-gray-300 hover:text-white`}
    >
      <div
        id="sidebar-resources-menu-header"
        className="flex flex-row ml-0 cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
        onKeyUp={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
      >
        <div className="w-full flex items-center ">
          <div className="flex items-center">
            <FiLayers size={24} className="ml-2 my-4 block" />
            <span className="ml-2">Ressources</span>
          </div>
          <OpenIcon className="ml-2 mt-1" />
        </div>
      </div>
      <div
        id="sidebar-resources-menu-contents"
        className={`text-sm collapsible-height ${
          isOpen ? 'h-auto opacity-100' : 'h-0 opacity-0 hidden'
        } transition ml-10`}
      >
        {children}
      </div>
    </div>
  )
}

function Support() {
  const { sideBarCollapsed } = useSelector(getLayoutState)
  const url = EXTERNAL_URLS.CONTACT

  return (
    <div className="text-gray-300 hover:text-white ml-1">
      {sideBarCollapsed ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
        >
          <FaRegQuestionCircle className="my-10 ml-1 transition-all" size={22} />
        </a>
      ) : (
        <div className="flex ml-1 transition-all">
          <FaRegQuestionCircle className="my-1" size={22} />
          <a
            href={url}
            className="ml-4 mt-1"
            target="_blank"
            rel="noreferrer"
          >
            Support
          </a>
        </div>
      )}
    </div>
  )
}

const SideBar = () => {
  const { sideBarCollapsed } = useSelector(getLayoutState)
  const contents = (
    <ul>
      <li className="ml-2 mb-1 hover:text-gray-300 cursor-pointer">
        <a
          href={import.meta.env.VITE_API_DOCS_BASE_URI}
          className="cursor-pointer"
          target="_blank"
          rel="noreferrer"
        >
          API Explorer
        </a>
      </li>
      {false && (
        <>
          <li className="ml-2 mb-1 hover:text-gray-300">Get started</li>
          <li className="ml-2 mb-1 hover:text-gray-300">Blog</li>
          <li className="ml-2 mb-1 hover:text-gray-300">Release notes</li>
        </>
      )}
      <li className="ml-2 mb-1 hover:text-gray-300">
        <a
          href={import.meta.env.VITE_TERMS_CONDITIONS_URL}
          target="_blank"
          rel="noreferrer"
        >
          Legal documents&nbsp;
          <span className="link-to-file">PDF</span>
        </a>
      </li>
      <li className="ml-2 mb-1 hover:text-gray-300">
        <a
          href={import.meta.env.VITE_PRIVACY_POLICY_URL}
          target="_blank"
          rel="noreferrer"
        >
          Privacy policy&nbsp;
          <span className="link-to-file">PDF</span>
        </a>
      </li>
    </ul>
  )

  return (
    <aside
      id="default-sidebar"
      className={`fixed top-0 left-0 z-40 ${
        sideBarCollapsed ? 'w-24' : 'w-64'
      } h-screen transition-all duration-500 select-none`}
      aria-label="Sidebar"
    >
      <div className="h-full pl-6 pr-2 py-4 overflow-y-clip bg-gray-0">
        <SideBarHeader />

        {/* Group 1: CTA */}
        <ul className="space-y-2 font-medium">
          <li key="cta">
            <SideBarCTA link="/job-creation" label="Create a new task" Icon={BiPlus} />
          </li>
        </ul>

        {/* Group 2: Home and Tasks */}
        <ul className="space-y-2 font-medium">
          <li key="home">
            <SideBarItem Icon={AiOutlineHome} link="/" label="Home" />
          </li>
          <li key="tasks">
            <SideBarItem Icon={BiBot} link="/jobs" label="Tasks" />
          </li>
        </ul>

        <SideBarSeparator />

        {/* Group 3: API Credentials and Webhooks */}
        <ul className="space-y-2 font-medium">
          <li key="api-credentials">
            <SideBarItem Icon={PiPuzzlePiece} link="/api-credentials" label="API Credentials" />
          </li>
          <li key="webhooks">
            <SideBarItem Icon={MdWebhook} link="/webhooks" label="Webhooks" />
          </li>
        </ul>

        <SideBarSeparator />

        {/* Group 4: Billing */}
        <ul className="space-y-2 font-medium">
          <li key="usage">
            <SideBarItem Icon={AiOutlineBarChart} link="/usage" label="Usage" />
          </li>
        </ul>

        <SideBarSeparator />

        {/* Group 5: Ressources and Support */}
        <ul className="space-y-2 font-medium">
          <li key="resources">
            {sideBarCollapsed ? (
              <CollapsedAddMenu>{contents}</CollapsedAddMenu>
            ) : (
              <SideBarAddMenu>{contents}</SideBarAddMenu>
            )}
          </li>
          <li key="support">
            <Support />
          </li>
        </ul>
      </div>
    </aside>
  )
}

export default SideBar
